import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Calendar } from '@/components/ui/calendar';
import { toast } from 'sonner';
import { Calendar as CalendarIcon, ShoppingBag, Users, LogOut, LayoutDashboard, Settings as SettingsIcon, Ban, TrendingUp, Scissors, UserCheck, Sparkles, Menu, X, ChevronLeft, ChevronRight, Plus, Lock, Clock, Gift } from 'lucide-react';
import { BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { format, addDays, subDays, startOfDay, isSameDay } from 'date-fns';
import { de } from 'date-fns/locale';
import { getHeroSlides } from '@/heroSlides';

const API = '/api';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8'];

// Helper function to get correct image URL
const getImageUrl = (url) => {
  if (!url) return null;
  // If it's already a full URL (Supabase Storage or external), return as-is
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }
  // If it's a local upload path, prepend the backend URL
  if (url.startsWith('/uploads')) {
    return `/api${url}`;
  }
  return url;
};

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { user, logout, getAuthHeaders } = useAuth();
  const [activeTab, setActiveTab] = useState('calendar');
  const [bookings, setBookings] = useState([]);
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState(null);
  const [blockedSlots, setBlockedSlots] = useState([]);
  const [stylists, setStylists] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [services, setServices] = useState([]);
  const [products, setProducts] = useState([]);
  const [settings, setSettings] = useState({});
  const [heroSlidesDraft, setHeroSlidesDraft] = useState([]);
  const [savingHeroSlides, setSavingHeroSlides] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  
  // Block slot modal
  const [isBlockModalOpen, setIsBlockModalOpen] = useState(false);
  const [blockDate, setBlockDate] = useState(null);
  const [blockTime, setBlockTime] = useState('');
  const [blockReason, setBlockReason] = useState('');

  // Stylist modal
  const [isStylistModalOpen, setIsStylistModalOpen] = useState(false);
  const [editingStylist, setEditingStylist] = useState(null);
  const [stylistForm, setStylistForm] = useState({
    name: '',
    email: '',
    bio: '',
    profile_image_url: ''
  });

  // Service modal
  const [isServiceModalOpen, setIsServiceModalOpen] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [serviceForm, setServiceForm] = useState({
    name: '',
    description: '',
    price: '',
    duration: '',
    image_url: ''
  });

  // Product modal
  const [isProductModalOpen, setIsProductModalOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [productForm, setProductForm] = useState({
    name: '',
    description: '',
    price: '',
    hair_type: 'alle',
    category: 'Haarpflege',
    image_url: '',
    in_stock: true
  });

  // Gallery state
  const [galleryImages, setGalleryImages] = useState([]);
  const [uploadingGallery, setUploadingGallery] = useState(false);
  const [galleryUploadProgress, setGalleryUploadProgress] = useState(null);

  // Stripe memberships and gift cards
  const [billingOverview, setBillingOverview] = useState({ memberships: [], gift_cards: [], payments: [], summary: {} });
  const [giftRedemption, setGiftRedemption] = useState({ code: '', amount: '' });
  const [redeemingGift, setRedeemingGift] = useState(false);

  // Customer modal
  const [isCustomerModalOpen, setIsCustomerModalOpen] = useState(false);
  const [customerForm, setCustomerForm] = useState({
    name: '',
    email: '',
    password: '',
    phone: '',
    hair_type: '',
    notes: ''
  });

  // Customer detail modal
  const [isCustomerDetailOpen, setIsCustomerDetailOpen] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [isEditingCustomer, setIsEditingCustomer] = useState(false);
  const [editCustomerForm, setEditCustomerForm] = useState({
    name: '',
    phone: '',
    hair_type: '',
    notes: ''
  });

  // Booking modal
  const [isBookingModalOpen, setIsBookingModalOpen] = useState(false);
  const [bookingForm, setBookingForm] = useState({
    customer_email: '',
    service_id: '',
    stylist_id: '',
    date: '',
    time: '',
    notes: ''
  });

  // Calendar view state
  const [calendarDate, setCalendarDate] = useState(new Date());
  const [calendarBookings, setCalendarBookings] = useState([]);

  // Time slots for calendar (8:00 - 20:00)
  const timeSlots = [];
  for (let hour = 8; hour <= 20; hour++) {
    timeSlots.push(`${hour.toString().padStart(2, '0')}:00`);
    if (hour < 20) {
      timeSlots.push(`${hour.toString().padStart(2, '0')}:30`);
    }
  }

  useEffect(() => {
    fetchData();
  }, [activeTab]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const headers = getAuthHeaders();
      
      if (activeTab === 'overview') {
        const statsRes = await axios.get(`${API}/dashboard/stats`, { headers });
        setStats(statsRes.data);
      }
      
      if (activeTab === 'bookings' || activeTab === 'overview') {
        const bookingsRes = await axios.get(`${API}/bookings/all`, { headers });
        setBookings(bookingsRes.data);
        
        // Load services and stylists for booking modal
        if (activeTab === 'bookings') {
          const servicesRes = await axios.get(`${API}/services`, { headers });
          setServices(servicesRes.data);
          const stylistsRes = await axios.get(`${API}/stylists/all`, { headers });
          setStylists(stylistsRes.data);
        }
      }

      if (activeTab === 'calendar') {
        const bookingsRes = await axios.get(`${API}/bookings/all`, { headers });
        setCalendarBookings(bookingsRes.data);
        const stylistsRes = await axios.get(`${API}/stylists/all`, { headers });
        setStylists(stylistsRes.data);
        const servicesRes = await axios.get(`${API}/services`, { headers });
        setServices(servicesRes.data);
      }
      
      if (activeTab === 'orders' || activeTab === 'overview') {
        const ordersRes = await axios.get(`${API}/orders/all`, { headers });
        setOrders(ordersRes.data);
      }
      
      if (activeTab === 'blocked') {
        const blockedRes = await axios.get(`${API}/blocked-slots`, { headers });
        setBlockedSlots(blockedRes.data);
      }

      if (activeTab === 'stylists') {
        const stylistsRes = await axios.get(`${API}/stylists/all`, { headers });
        setStylists(stylistsRes.data);
      }

      if (activeTab === 'customers') {
        const customersRes = await axios.get(`${API}/admin/customers`, { headers });
        setCustomers(customersRes.data);
      }

      if (activeTab === 'billing') {
        const billingRes = await axios.get(`${API}/admin/billing/overview`, { headers });
        setBillingOverview(billingRes.data);
      }

      if (activeTab === 'services') {
        const servicesRes = await axios.get(`${API}/services`, { headers });
        setServices(servicesRes.data);
      }

      if (activeTab === 'products') {
        const productsRes = await axios.get(`${API}/products`);
        setProducts(productsRes.data);
      }
      
      if (activeTab === 'settings') {
        const settingsRes = await axios.get(`${API}/settings`);
        setSettings(settingsRes.data);
        setHeroSlidesDraft(getHeroSlides(settingsRes.data));
        // Also load services for settings page
        const servicesRes = await axios.get(`${API}/services`, { headers });
        setServices(servicesRes.data);
        // Load gallery images
        try {
          const galleryRes = await axios.get(`${API}/gallery`);
          setGalleryImages(galleryRes.data);
        } catch (e) {
          console.log('Gallery not available yet');
          setGalleryImages([]);
        }
      }
    } catch (error) {
      toast.error('Fehler beim Laden der Daten');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const handleGiftRedemption = async (event) => {
    event.preventDefault();
    if (!giftRedemption.code.trim() || Number(giftRedemption.amount) <= 0) {
      toast.error('Bitte Gutscheincode und Einlösebetrag eingeben');
      return;
    }
    setRedeemingGift(true);
    try {
      const response = await axios.post(
        `${API}/admin/gift-cards/redeem`,
        { code: giftRedemption.code.trim().toUpperCase(), amount: Number(giftRedemption.amount) },
        { headers: getAuthHeaders() }
      );
      toast.success(`${Number(response.data.redeemed).toFixed(2).replace('.', ',')} € eingelöst – Restguthaben: ${Number(response.data.balance).toFixed(2).replace('.', ',')} €`);
      setGiftRedemption({ code: '', amount: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gutschein konnte nicht eingelöst werden');
    } finally {
      setRedeemingGift(false);
    }
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setIsMobileMenuOpen(false); // Close mobile menu when tab is selected
  };

  const handleBlockSlot = async () => {
    if (!blockDate) {
      toast.error('Bitte wählen Sie ein Datum');
      return;
    }

    try {
      const dateStr = format(blockDate, 'yyyy-MM-dd');
      await axios.post(
        `${API}/blocked-slots`,
        {
          date: dateStr,
          time: blockTime || null,
          reason: blockReason || 'Gesperrt'
        },
        { headers: getAuthHeaders() }
      );
      
      toast.success(blockTime ? 'Zeitslot gesperrt' : 'Ganzer Tag gesperrt');
      setIsBlockModalOpen(false);
      setBlockDate(null);
      setBlockTime('');
      setBlockReason('');
      fetchData();
    } catch (error) {
      toast.error('Fehler beim Sperren');
    }
  };

  const handleUnblockSlot = async (slotId) => {
    try {
      await axios.delete(`${API}/blocked-slots/${slotId}`, { headers: getAuthHeaders() });
      toast.success('Sperrung aufgehoben');
      fetchData();
    } catch (error) {
      toast.error('Fehler beim Aufheben der Sperrung');
    }
  };

  const handleUpdateSetting = async (key, value) => {
    try {
      await axios.put(`${API}/settings`, { key, value }, { headers: getAuthHeaders() });
      toast.success('Einstellung aktualisiert');
      fetchData();
    } catch (error) {
      toast.error('Fehler beim Aktualisieren');
    }
  };

  // Gallery functions
  const handleGalleryUpload = async (event) => {
    const input = event.target;
    const selectedFiles = Array.from(input.files || []);
    input.value = '';

    if (selectedFiles.length === 0) return;

    const allowedTypes = new Set(['image/jpeg', 'image/jpg', 'image/png', 'image/webp']);
    const validFiles = selectedFiles.filter((file) => allowedTypes.has(file.type));
    const skippedFiles = selectedFiles.filter((file) => !allowedTypes.has(file.type));

    if (validFiles.length === 0) {
      toast.error('Bitte wählen Sie Bilder im Format JPEG, PNG oder WEBP aus.');
      return;
    }

    setUploadingGallery(true);
    setGalleryUploadProgress({ completed: 0, total: validFiles.length, failed: 0 });

    let completed = 0;
    let failed = 0;
    const failedFiles = [];

    const uploadFile = async (file) => {
      const formData = new FormData();
      formData.append('file', file);

      try {
        await axios.post(`${API}/gallery/upload`, formData, {
          headers: {
            ...getAuthHeaders(),
            'Content-Type': 'multipart/form-data',
          },
        });
        return true;
      } catch (error) {
        failedFiles.push({
          name: file.name,
          message: error.response?.data?.detail || 'Upload fehlgeschlagen',
        });
        return false;
      }
    };

    try {
      // Kleine parallele Gruppen halten den Upload schnell, ohne den Server zu überlasten.
      for (let index = 0; index < validFiles.length; index += 3) {
        const batch = validFiles.slice(index, index + 3);
        const results = await Promise.all(batch.map(uploadFile));

        completed += results.length;
        failed += results.filter((success) => !success).length;
        setGalleryUploadProgress({
          completed,
          total: validFiles.length,
          failed,
        });
      }

      const uploaded = validFiles.length - failed;

      if (uploaded > 0) {
        await fetchData();
        toast.success(
          uploaded === 1
            ? '1 Bild erfolgreich hochgeladen'
            : `${uploaded} Bilder erfolgreich hochgeladen`
        );
      }

      const notUploaded = [
        ...skippedFiles.map((file) => file.name),
        ...failedFiles.map((file) => file.name),
      ];

      if (notUploaded.length > 0) {
        const preview = notUploaded.slice(0, 3).join(', ');
        const remaining = notUploaded.length - 3;
        toast.error(
          `${notUploaded.length} Datei${notUploaded.length === 1 ? '' : 'en'} nicht hochgeladen: ${preview}${remaining > 0 ? ` und ${remaining} weitere` : ''}`
        );
      }
    } finally {
      setUploadingGallery(false);
      setGalleryUploadProgress(null);
    }
  };

  const handleGalleryDelete = async (imageId) => {
    if (!window.confirm('Möchten Sie dieses Bild wirklich löschen?')) return;

    try {
      await axios.delete(`${API}/gallery/${imageId}`, { headers: getAuthHeaders() });
      toast.success('Bild gelöscht');
      fetchData();
    } catch (error) {
      toast.error('Fehler beim Löschen');
    }
  };

  const saveHeroSlides = async (slides) => {
    const cleanSlides = slides.map((url) => url.trim());
    if (cleanSlides.length < 1 || cleanSlides.length > 5 || cleanSlides.some((url) => !/^(https?:\/\/|\/uploads\/)/i.test(url))) {
      toast.error('Bitte 1 bis 5 gültige Bild-URLs eingeben (https:// oder /uploads/).');
      return false;
    }
    setSavingHeroSlides(true);
    try {
      await axios.put(`${API}/settings`, { key: 'hero_slides', value: JSON.stringify(cleanSlides) }, { headers: getAuthHeaders() });
      setHeroSlidesDraft(cleanSlides);
      setSettings((current) => ({ ...current, hero_slides: JSON.stringify(cleanSlides) }));
      toast.success('Banner-Slideshow gespeichert');
      return true;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Slideshow konnte nicht gespeichert werden');
      return false;
    } finally {
      setSavingHeroSlides(false);
    }
  };

  const handleHeroImageUpload = async (event, index) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setSavingHeroSlides(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const response = await axios.post(`${API}/settings/upload-hero-image`, formData, {
        headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' },
      });
      const updated = [...heroSlidesDraft];
      updated[index] = response.data.url;
      setHeroSlidesDraft(updated);
      await saveHeroSlides(updated);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Hochladen');
    } finally {
      setSavingHeroSlides(false);
      event.target.value = '';
    }
  };

  const moveHeroSlide = (index, offset) => {
    const updated = [...heroSlidesDraft];
    [updated[index], updated[index + offset]] = [updated[index + offset], updated[index]];
    setHeroSlidesDraft(updated);
  };

  const openStylistModal = (stylist = null) => {
    if (stylist) {
      setEditingStylist(stylist);
      setStylistForm({
        name: stylist.name,
        email: stylist.email || '',
        bio: stylist.bio || '',
        profile_image_url: stylist.profile_image_url || ''
      });
    } else {
      setEditingStylist(null);
      setStylistForm({
        name: '',
        email: '',
        bio: '',
        profile_image_url: ''
      });
    }
    setIsStylistModalOpen(true);
  };

  const handleImageUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API}/stylists/upload-image`, formData, {
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'multipart/form-data',
        },
      });
      setStylistForm({ ...stylistForm, profile_image_url: response.data.url });
      toast.success('Bild hochgeladen');
    } catch (error) {
      toast.error('Fehler beim Hochladen des Bildes');
    }
  };

  const handleSaveStylist = async () => {
    if (!stylistForm.name) {
      toast.error('Bitte geben Sie einen Namen ein');
      return;
    }

    try {
      if (editingStylist) {
        await axios.put(
          `${API}/stylists/${editingStylist.id}`,
          stylistForm,
          { headers: getAuthHeaders() }
        );
        toast.success('Stylist aktualisiert');
      } else {
        await axios.post(
          `${API}/stylists`,
          stylistForm,
          { headers: getAuthHeaders() }
        );
        toast.success('Stylist hinzugefügt');
      }
      setIsStylistModalOpen(false);
      fetchData();
    } catch (error) {
      toast.error('Fehler beim Speichern');
    }
  };

  const handleDeleteStylist = async (stylistId) => {
    if (!window.confirm('Möchten Sie diesen Stylisten wirklich löschen?')) {
      return;
    }

    try {
      await axios.delete(`${API}/stylists/${stylistId}`, { headers: getAuthHeaders() });
      toast.success('Stylist gelöscht');
      fetchData();
    } catch (error) {
      toast.error('Fehler beim Löschen');
    }
  };

  const openServiceModal = (service = null) => {
    if (service) {
      setEditingService(service);
      setServiceForm({
        name: service.name,
        description: service.description || '',
        price: service.price.toString(),
        duration: service.duration.toString(),
        image_url: service.image_url || ''
      });
    } else {
      setEditingService(null);
      setServiceForm({
        name: '',
        description: '',
        price: '',
        duration: '',
        image_url: ''
      });
    }
    setIsServiceModalOpen(true);
  };

  const handleServiceImageUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API}/services/upload-image`, formData, {
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'multipart/form-data',
        },
      });
      setServiceForm({ ...serviceForm, image_url: response.data.url });
      toast.success('Bild hochgeladen');
    } catch (error) {
      toast.error('Fehler beim Hochladen des Bildes');
    }
  };

  const handleSaveService = async () => {
    if (!serviceForm.name || !serviceForm.price || !serviceForm.duration) {
      toast.error('Bitte füllen Sie alle Pflichtfelder aus');
      return;
    }

    try {
      await axios.put(
        `${API}/services/${editingService.id}`,
        {
          name: serviceForm.name,
          description: serviceForm.description,
          price: parseFloat(serviceForm.price),
          duration: parseInt(serviceForm.duration),
          image_url: serviceForm.image_url
        },
        { headers: getAuthHeaders() }
      );
      toast.success('Service aktualisiert');
      setIsServiceModalOpen(false);
      fetchData();
    } catch (error) {
      toast.error('Fehler beim Speichern');
    }
  };

  const handleSaveCustomer = async () => {
    if (!customerForm.name || !customerForm.email || !customerForm.password) {
      toast.error('Name, E-Mail und Passwort sind Pflichtfelder');
      return;
    }

    try {
      await axios.post(
        `${API}/auth/register`,
        customerForm
      );
      toast.success('Kunde erfolgreich angelegt');
      setIsCustomerModalOpen(false);
      setCustomerForm({
        name: '',
        email: '',
        password: '',
        phone: '',
        hair_type: '',
        notes: ''
      });
      fetchData();
    } catch (error) {
      toast.error('Fehler beim Anlegen des Kunden');
    }
  };

  const handleUpdateCustomer = async () => {
    if (!editCustomerForm.name) {
      toast.error('Name ist ein Pflichtfeld');
      return;
    }

    try {
      await axios.put(
        `${API}/admin/customers/${selectedCustomer.id}`,
        editCustomerForm,
        { headers: getAuthHeaders() }
      );
      toast.success('Kundendaten aktualisiert');
      setIsEditingCustomer(false);
      setIsCustomerDetailOpen(false);
      fetchData();
    } catch (error) {
      toast.error('Fehler beim Aktualisieren');
    }
  };

  const handleSaveBooking = async () => {
    if (!bookingForm.customer_email || !bookingForm.service_id || !bookingForm.stylist_id || !bookingForm.date || !bookingForm.time) {
      toast.error('Alle Felder sind Pflichtfelder');
      return;
    }

    try {
      await axios.post(
        `${API}/admin/bookings`,
        bookingForm,
        { headers: getAuthHeaders() }
      );
      toast.success('Termin erfolgreich angelegt');
      setIsBookingModalOpen(false);
      setBookingForm({
        customer_email: '',
        service_id: '',
        stylist_id: '',
        date: '',
        time: '',
        notes: ''
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Anlegen des Termins');
    }
  };

  // Product functions
  const openProductModal = (product = null) => {
    if (product) {
      setEditingProduct(product);
      setProductForm({
        name: product.name,
        description: product.description || '',
        price: product.price.toString(),
        hair_type: product.hair_type || 'alle',
        category: product.category || 'Haarpflege',
        image_url: product.image_url || '',
        in_stock: product.in_stock !== false
      });
    } else {
      setEditingProduct(null);
      setProductForm({
        name: '',
        description: '',
        price: '',
        hair_type: 'alle',
        category: 'Haarpflege',
        image_url: '',
        in_stock: true
      });
    }
    setIsProductModalOpen(true);
  };

  const handleProductImageUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API}/products/upload-image`, formData, {
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'multipart/form-data',
        },
      });
      setProductForm({ ...productForm, image_url: response.data.url });
      toast.success('Bild hochgeladen');
    } catch (error) {
      toast.error('Fehler beim Hochladen des Bildes');
    }
  };

  const handleSaveProduct = async () => {
    if (!productForm.name || !productForm.price) {
      toast.error('Name und Preis sind Pflichtfelder');
      return;
    }

    try {
      if (editingProduct) {
        await axios.put(
          `${API}/products/${editingProduct.id}`,
          null,
          {
            headers: getAuthHeaders(),
            params: {
              name: productForm.name,
              description: productForm.description,
              price: parseFloat(productForm.price),
              hair_type: productForm.hair_type,
              category: productForm.category,
              image_url: productForm.image_url,
              in_stock: productForm.in_stock
            }
          }
        );
        toast.success('Produkt aktualisiert');
      } else {
        await axios.post(
          `${API}/products`,
          {
            name: productForm.name,
            description: productForm.description,
            price: parseFloat(productForm.price),
            hair_type: productForm.hair_type,
            category: productForm.category,
            image_url: productForm.image_url,
            in_stock: productForm.in_stock
          },
          { headers: getAuthHeaders() }
        );
        toast.success('Produkt erstellt');
      }
      setIsProductModalOpen(false);
      fetchData();
    } catch (error) {
      toast.error('Fehler beim Speichern des Produkts');
    }
  };

  const handleDeleteProduct = async (productId) => {
    if (!window.confirm('Möchten Sie dieses Produkt wirklich löschen?')) return;

    try {
      await axios.delete(`${API}/products/${productId}`, { headers: getAuthHeaders() });
      toast.success('Produkt gelöscht');
      fetchData();
    } catch (error) {
      toast.error('Fehler beim Löschen des Produkts');
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      confirmed: 'bg-green-100 text-green-700',
      cancelled: 'bg-red-100 text-red-700',
      completed: 'bg-blue-100 text-blue-700',
      pending: 'bg-yellow-100 text-yellow-700',
      processing: 'bg-blue-100 text-blue-700',
      shipped: 'bg-purple-100 text-purple-700',
      delivered: 'bg-green-100 text-green-700',
    };
    return colors[status] || 'bg-gray-100 text-gray-700';
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 h-16 bg-slate-900 text-white flex items-center justify-between px-4 z-50">
        <div className="flex items-center space-x-3">
          <img 
            src="https://customer-assets.emergentagent.com/job_friseur-booking/artifacts/mtmj2lif_IMG_5702.PNG" 
            alt="Salon Galo Logo" 
            className="h-8 w-auto brightness-0 invert"
          />
          <span className="font-semibold">Admin</span>
        </div>
        <Button
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          variant="ghost"
          size="sm"
          className="text-white hover:bg-slate-800"
        >
          {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </Button>
      </div>

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-40"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar - Hidden when calendar is active */}
      <div className={`fixed top-0 left-0 h-full w-64 bg-slate-900 text-white p-6 z-50 transform transition-transform duration-300 ease-in-out ${
        isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'
      } ${activeTab === 'calendar' ? 'lg:-translate-x-full' : 'lg:translate-x-0'}`}>
        <div className="mb-8">
          <img 
            src="https://customer-assets.emergentagent.com/job_friseur-booking/artifacts/mtmj2lif_IMG_5702.PNG" 
            alt="Salon Galo Logo" 
            className="h-12 w-auto mb-4 brightness-0 invert"
          />
          <p className="text-sm text-slate-400">Willkommen, {user?.name}</p>
        </div>

        <nav className="space-y-2">
          <button
            onClick={() => handleTabChange('calendar')}
            className={`w-full flex items-center space-x-3 p-3 rounded-lg transition-colors ${activeTab === 'calendar' ? 'bg-blue-600' : 'hover:bg-slate-800'}`}
          >
            <CalendarIcon className="w-5 h-5" />
            <span className="font-medium">Kalender</span>
          </button>
          <button
            onClick={() => handleTabChange('overview')}
            className={`w-full flex items-center space-x-3 p-3 rounded-lg transition-colors ${activeTab === 'overview' ? 'bg-blue-600' : 'hover:bg-slate-800'}`}
          >
            <LayoutDashboard className="w-5 h-5" />
            <span className="font-medium">Übersicht</span>
          </button>
          <button
            onClick={() => handleTabChange('bookings')}
            className={`w-full flex items-center space-x-3 p-3 rounded-lg transition-colors ${activeTab === 'bookings' ? 'bg-blue-600' : 'hover:bg-slate-800'}`}
          >
            <Users className="w-5 h-5" />
            <span className="font-medium">Buchungen</span>
          </button>
          <button
            onClick={() => handleTabChange('blocked')}
            className={`w-full flex items-center space-x-3 p-3 rounded-lg transition-colors ${activeTab === 'blocked' ? 'bg-blue-600' : 'hover:bg-slate-800'}`}
          >
            <Ban className="w-5 h-5" />
            <span className="font-medium">Gesperrte Zeiten</span>
          </button>
          <button
            onClick={() => handleTabChange('services')}
            className={`w-full flex items-center space-x-3 p-3 rounded-lg transition-colors ${activeTab === 'services' ? 'bg-blue-600' : 'hover:bg-slate-800'}`}
          >
            <Sparkles className="w-5 h-5" />
            <span className="font-medium">Services</span>
          </button>
          <button
            onClick={() => handleTabChange('stylists')}
            className={`w-full flex items-center space-x-3 p-3 rounded-lg transition-colors ${activeTab === 'stylists' ? 'bg-blue-600' : 'hover:bg-slate-800'}`}
          >
            <Scissors className="w-5 h-5" />
            <span className="font-medium">Stylisten</span>
          </button>
          <button
            onClick={() => handleTabChange('customers')}
            className={`w-full flex items-center space-x-3 p-3 rounded-lg transition-colors ${activeTab === 'customers' ? 'bg-blue-600' : 'hover:bg-slate-800'}`}
          >
            <UserCheck className="w-5 h-5" />
            <span className="font-medium">Kunden</span>
          </button>
          <button
            onClick={() => handleTabChange('billing')}
            className={`w-full flex items-center space-x-3 p-3 rounded-lg transition-colors ${activeTab === 'billing' ? 'bg-blue-600' : 'hover:bg-slate-800'}`}
          >
            <Gift className="w-5 h-5" />
            <span className="font-medium">Abos & Gutscheine</span>
          </button>
          {user?.role === 'super_admin' && (
            <button
              onClick={() => handleTabChange('settings')}
              className={`w-full flex items-center space-x-3 p-3 rounded-lg transition-colors ${activeTab === 'settings' ? 'bg-blue-600' : 'hover:bg-slate-800'}`}
            >
              <SettingsIcon className="w-5 h-5" />
              <span className="font-medium">Einstellungen</span>
            </button>
          )}
        </nav>

        <div className="absolute bottom-6 left-6 right-6">
          <Button
            onClick={handleLogout}
            variant="ghost"
            className="w-full justify-start text-slate-300 hover:text-white hover:bg-slate-800"
            data-testid="admin-logout-button"
          >
            <LogOut className="w-5 h-5 mr-3" />
            Abmelden
          </Button>
        </div>
      </div>

      {/* Main Content */}
      <div className={`pt-16 lg:pt-0 p-4 lg:p-8 ${activeTab === 'calendar' ? 'lg:ml-0' : 'lg:ml-64'}`}>
        {/* Calendar Tab - Fresha Style */}
        {activeTab === 'calendar' && (
          <div className="flex -ml-4 lg:-ml-8 -mr-4 lg:-mr-8 -mt-4 lg:-mt-0">
            {/* Left Sidebar with Calendar */}
            <div className="w-[250px] min-w-[250px] flex-shrink-0 bg-white border-r-2 border-slate-300 hidden lg:flex lg:flex-col h-screen sticky top-0 z-20">
              {/* Top Section */}
              <div className="p-4 flex-1 overflow-hidden">
                {/* Salon Name */}
                <div className="mb-4">
                  <h2 className="text-lg font-semibold text-slate-900">Salon Galo</h2>
                  <p className="text-sm text-slate-500">{user?.name}</p>
                </div>

                {/* Add Button */}
                <Button 
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white mb-3"
                  onClick={() => {
                    setBookingForm({
                      ...bookingForm,
                      date: format(calendarDate, 'yyyy-MM-dd')
                    });
                    setIsBookingModalOpen(true);
                  }}
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Hinzufügen
                </Button>

                {/* Mini Calendar - Larger */}
                <div className="border rounded-lg p-2 bg-white">
                  <div className="text-center py-2 font-medium text-sm flex items-center justify-between px-2">
                    <button 
                      onClick={() => setCalendarDate(new Date(calendarDate.getFullYear(), calendarDate.getMonth() - 1, calendarDate.getDate()))}
                      className="p-1 hover:bg-slate-100 rounded"
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </button>
                    <span className="font-semibold">{format(calendarDate, 'MMMM yyyy', { locale: de })}</span>
                    <button 
                      onClick={() => setCalendarDate(new Date(calendarDate.getFullYear(), calendarDate.getMonth() + 1, calendarDate.getDate()))}
                      className="p-1 hover:bg-slate-100 rounded"
                    >
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                  <div className="grid grid-cols-7 gap-1 text-center">
                    {['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'].map(day => (
                      <div key={day} className="p-1 text-slate-500 font-medium text-xs">{day}</div>
                    ))}
                    {(() => {
                      const firstDay = new Date(calendarDate.getFullYear(), calendarDate.getMonth(), 1);
                      const lastDay = new Date(calendarDate.getFullYear(), calendarDate.getMonth() + 1, 0);
                      const startDay = (firstDay.getDay() + 6) % 7; // Monday = 0
                      const days = [];
                      
                      // Empty cells before first day
                      for (let i = 0; i < startDay; i++) {
                        days.push(<div key={`empty-${i}`} className="p-1 h-8"></div>);
                      }
                      
                      // Days of month
                      for (let d = 1; d <= lastDay.getDate(); d++) {
                        const isSelected = d === calendarDate.getDate();
                        const isToday = d === new Date().getDate() && 
                                       calendarDate.getMonth() === new Date().getMonth() &&
                                       calendarDate.getFullYear() === new Date().getFullYear();
                        days.push(
                          <button
                            key={d}
                            onClick={() => setCalendarDate(new Date(calendarDate.getFullYear(), calendarDate.getMonth(), d))}
                            className={`h-8 w-8 text-sm rounded-full hover:bg-slate-100 transition-colors ${
                              isSelected ? 'bg-slate-900 text-white hover:bg-slate-800' : ''
                            } ${isToday && !isSelected ? 'bg-blue-100 text-blue-700' : ''}`}
                          >
                            {d}
                          </button>
                        );
                      }
                      return days;
                    })()}
                  </div>
                </div>
              </div>

              {/* Bottom Section - Dashboard & Logout */}
              <div className="p-4 border-t border-slate-200 bg-white">
                <button
                  onClick={() => handleTabChange('overview')}
                  className="flex items-center space-x-2 text-slate-600 hover:text-slate-900 w-full p-2 rounded-lg hover:bg-slate-50"
                >
                  <LayoutDashboard className="w-5 h-5" />
                  <span>Dashboard</span>
                </button>
                <button
                  onClick={handleLogout}
                  className="flex items-center space-x-2 text-red-500 hover:text-red-600 w-full p-2 rounded-lg hover:bg-red-50 mt-1"
                >
                  <LogOut className="w-5 h-5" />
                  <span>Abmelden</span>
                </button>
              </div>
            </div>

            {/* Main Calendar Area */}
            <div className="flex-1 bg-white min-h-screen">
              {/* Calendar Header */}
              <div className="flex items-center justify-between p-4 border-b border-slate-200 bg-white sticky top-0 z-10">
                <div className="flex items-center space-x-2">
                  <Button
                    variant="outline"
                    size="icon"
                    className="h-9 w-9"
                    onClick={() => setCalendarDate(subDays(calendarDate, 1))}
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="icon"
                    className="h-9 w-9"
                    onClick={() => setCalendarDate(addDays(calendarDate, 1))}
                  >
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCalendarDate(new Date())}
                  >
                    Heute
                  </Button>
                </div>

                <div className="text-center">
                  <h1 className="text-2xl font-semibold text-slate-900">
                    {format(calendarDate, 'EEEE', { locale: de })}
                  </h1>
                  <p className="text-sm text-slate-500">
                    {format(calendarDate, 'd. MMMM yyyy', { locale: de })}
                  </p>
                </div>

                <div className="flex items-center space-x-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setIsBlockModalOpen(true)}
                  >
                    <Ban className="w-4 h-4 mr-2" />
                    Sperren
                  </Button>
                  <Button
                    size="sm"
                    className="bg-blue-600 hover:bg-blue-700"
                    onClick={() => {
                      setBookingForm({
                        ...bookingForm,
                        date: format(calendarDate, 'yyyy-MM-dd')
                      });
                      setIsBookingModalOpen(true);
                    }}
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Termin
                  </Button>
                </div>
              </div>

              {/* Calendar Grid */}
              <div className="overflow-y-auto" style={{ height: 'calc(100vh - 73px)' }}>
                <div className="min-w-[800px]">
                  {/* Stylist Header */}
                  <div className="grid border-b border-slate-200 bg-white sticky top-0 z-10" style={{ gridTemplateColumns: `70px repeat(${stylists.length + 1}, 1fr)` }}>
                    <div className="p-3 border-r border-slate-200 text-xs font-medium text-slate-500 uppercase">
                      Zeit
                    </div>
                    {stylists.map((stylist, idx) => (
                      <div key={stylist.id} className="p-3 border-r border-slate-200 flex items-center space-x-3">
                        <div className="w-10 h-10 rounded-full overflow-hidden bg-slate-200 flex-shrink-0">
                          {stylist.profile_image_url ? (
                            <img 
                              src={getImageUrl(stylist.profile_image_url)}
                              alt={stylist.name}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-slate-500 text-sm font-medium bg-slate-200">
                              {stylist.name.charAt(0)}
                            </div>
                          )}
                        </div>
                        <div>
                          <p className="text-sm font-medium text-slate-900">{stylist.name}</p>
                          <p className="text-xs text-slate-500">Stylist</p>
                        </div>
                      </div>
                    ))}
                    <div className="p-3 flex items-center space-x-3">
                      <div className="w-10 h-10 rounded-full bg-slate-200 flex items-center justify-center flex-shrink-0">
                        <span className="text-slate-400 text-lg">?</span>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-slate-900">Nicht zugewiesen</p>
                        <p className="text-xs text-slate-500">Offen</p>
                      </div>
                    </div>
                  </div>

                  {/* Time Slots */}
                  {timeSlots.map((time, idx) => {
                    const dateStr = format(calendarDate, 'yyyy-MM-dd');
                    const bookingsAtTime = calendarBookings.filter(
                      b => b.date === dateStr && b.time === time && b.status === 'confirmed'
                    );
                    
                    // Get service duration for each booking
                    const getServiceDuration = (booking) => {
                      const service = services.find(s => s.id === booking.service_id);
                      return service?.duration || 30;
                    };

                    // Get color for stylist
                    const getStylistColor = (stylistId, stylistIndex) => {
                      if (!stylistId) return { bg: 'bg-pink-50', border: 'border-pink-200', text: 'text-pink-700', badge: 'bg-pink-500' };
                      if (stylistIndex === 0) return { bg: 'bg-green-50', border: 'border-green-200', text: 'text-green-700', badge: 'bg-green-500' };
                      if (stylistIndex === 1) return { bg: 'bg-violet-50', border: 'border-violet-200', text: 'text-violet-700', badge: 'bg-violet-500' };
                      return { bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-700', badge: 'bg-blue-500' };
                    };
                    
                    return (
                      <div 
                        key={time} 
                        className="grid border-b border-slate-100"
                        style={{ gridTemplateColumns: `70px repeat(${stylists.length + 1}, 1fr)` }}
                      >
                        <div className="p-2 border-r border-slate-200 text-sm text-slate-500">
                          {time}
                        </div>
                        {stylists.map((stylist, stylistIdx) => {
                          const booking = bookingsAtTime.find(b => b.stylist_id === stylist.id);
                          const colors = getStylistColor(stylist.id, stylistIdx);
                          const duration = booking ? getServiceDuration(booking) : 30;
                          const heightMultiplier = Math.ceil(duration / 30);
                          
                          return (
                            <div 
                              key={stylist.id} 
                              className="border-r border-slate-100 min-h-[50px] relative p-1"
                            >
                              {booking && (
                                <div 
                                  className={`${colors.bg} ${colors.border} border rounded-lg p-2 cursor-pointer hover:shadow-md transition-shadow relative`}
                                  style={{ minHeight: `${heightMultiplier * 48}px` }}
                                >
                                  <div className="flex justify-between items-start">
                                    <div className="flex-1 min-w-0">
                                      <p className={`text-sm font-semibold ${colors.text}`}>{booking.service_name}</p>
                                      <p className="text-xs text-slate-600 truncate">{booking.user_name}</p>
                                      {duration > 30 && (
                                        <p className="text-xs text-slate-500 mt-1 flex items-center">
                                          <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <circle cx="12" cy="12" r="10"></circle>
                                            <path d="M12 6v6l4 2"></path>
                                          </svg>
                                          {duration} Min
                                        </p>
                                      )}
                                    </div>
                                    <span className={`${colors.badge} text-white text-xs font-medium px-2 py-1 rounded-full ml-2 flex-shrink-0`}>
                                      {booking.time}
                                    </span>
                                  </div>
                                </div>
                              )}
                              {/* Add button on hover */}
                              {!booking && idx === Math.floor(timeSlots.length / 2) && stylistIdx === Math.floor(stylists.length / 2) && (
                                <button 
                                  onClick={() => {
                                    setBookingForm({
                                      ...bookingForm,
                                      date: format(calendarDate, 'yyyy-MM-dd'),
                                      time: time,
                                      stylist_id: stylist.id
                                    });
                                    setIsBookingModalOpen(true);
                                  }}
                                  className="absolute inset-0 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity"
                                >
                                  <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 hover:bg-blue-200">
                                    <Plus className="w-5 h-5" />
                                  </div>
                                </button>
                              )}
                            </div>
                          );
                        })}
                        {/* Nicht zugewiesen column */}
                        <div className="min-h-[50px] relative p-1">
                          {bookingsAtTime.filter(b => !b.stylist_id).map((booking) => {
                            const colors = getStylistColor(null, -1);
                            const duration = getServiceDuration(booking);
                            const heightMultiplier = Math.ceil(duration / 30);
                            
                            return (
                              <div 
                                key={booking.id}
                                className={`${colors.bg} ${colors.border} border rounded-lg p-2 cursor-pointer hover:shadow-md transition-shadow mb-1`}
                                style={{ minHeight: `${heightMultiplier * 48}px` }}
                              >
                                <div className="flex justify-between items-start">
                                  <div className="flex-1 min-w-0">
                                    <p className={`text-sm font-semibold ${colors.text}`}>{booking.service_name}</p>
                                    <p className="text-xs text-slate-600 truncate">{booking.user_name}</p>
                                  </div>
                                  <span className={`${colors.badge} text-white text-xs font-medium px-2 py-1 rounded-full ml-2 flex-shrink-0`}>
                                    {booking.time}
                                  </span>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Overview Tab */}
        {activeTab === 'overview' && stats && (
          <div>
            <div className="mb-6 lg:mb-8">
              <h1 className="text-2xl lg:text-4xl font-semibold tracking-tight text-slate-900 mb-2">Dashboard Übersicht</h1>
              <p className="text-sm lg:text-base text-slate-600">Statistiken und Analysen auf einen Blick</p>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              <Card className="p-6 border-slate-100">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-600 mb-1">Gesamtbuchungen</p>
                    <p className="text-3xl font-bold text-slate-900">{stats.total_bookings}</p>
                    <p className="text-sm text-green-600 mt-1">+{stats.bookings_this_week} diese Woche</p>
                  </div>
                  <div className="w-12 h-12 rounded-full flex items-center justify-center bg-blue-100 text-blue-600">
                    <CalendarIcon className="w-6 h-6" />
                  </div>
                </div>
              </Card>
              
              <Card className="p-6 border-slate-100">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-600 mb-1">Gesamtbestellungen</p>
                    <p className="text-3xl font-bold text-slate-900">{stats.total_orders}</p>
                  </div>
                  <div className="w-12 h-12 rounded-full flex items-center justify-center bg-green-100 text-green-600">
                    <ShoppingBag className="w-6 h-6" />
                  </div>
                </div>
              </Card>
              
              <Card className="p-6 border-slate-100">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-600 mb-1">Gesamtumsatz</p>
                    <p className="text-3xl font-bold text-slate-900">€{stats.total_revenue.toFixed(2)}</p>
                    <p className="text-sm text-blue-600 mt-1">€{stats.revenue_this_month.toFixed(2)} diesen Monat</p>
                  </div>
                  <div className="w-12 h-12 rounded-full flex items-center justify-center bg-purple-100 text-purple-600">
                    <TrendingUp className="w-6 h-6" />
                  </div>
                </div>
              </Card>
            </div>

            {/* Charts */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
              <Card className="p-6 border-slate-100">
                <h2 className="text-xl font-semibold mb-4">Buchungen der letzten 7 Tage</h2>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={stats.bookings_by_day}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" />
                    <YAxis />
                    <Tooltip />
                    <Bar dataKey="count" fill="#0066FF" />
                  </BarChart>
                </ResponsiveContainer>
              </Card>

              <Card className="p-6 border-slate-100">
                <h2 className="text-xl font-semibold mb-4">Umsatz nach Service</h2>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={stats.revenue_by_service}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={(entry) => `${entry.name}: €${entry.value.toFixed(0)}`}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {stats.revenue_by_service.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </Card>
            </div>

            {/* Popular Services */}
            <Card className="p-6 border-slate-100">
              <h2 className="text-xl font-semibold mb-4">Beliebteste Services</h2>
              <div className="space-y-3">
                {stats.popular_services.map((service, idx) => (
                  <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <span className="font-medium">{service.name}</span>
                    <Badge className="bg-blue-100 text-blue-700">{service.count} Buchungen</Badge>
                  </div>
                ))}
              </div>
            </Card>
          </div>
        )}

        {/* Bookings Tab */}
        {activeTab === 'bookings' && (
          <div>
            <div className="mb-6 lg:mb-8">
              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
                <div>
                  <h1 className="text-2xl lg:text-4xl font-semibold tracking-tight text-slate-900 mb-2">Alle Buchungen</h1>
                  <p className="text-sm lg:text-base text-slate-600">Verwalten Sie alle Kundentermine</p>
                </div>
                <Button
                  onClick={() => setIsBookingModalOpen(true)}
                  className="bg-blue-600 hover:bg-blue-700 text-white w-full sm:w-auto"
                >
                  Termin anlegen
                </Button>
              </div>
            </div>

            <Card className="border-slate-100">
              <div className="p-3 lg:p-6">
                {bookings.length === 0 ? (
                  <p className="text-slate-600 text-center py-8">Keine Buchungen vorhanden</p>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Kunde</TableHead>
                        <TableHead>Service</TableHead>
                        <TableHead>Datum</TableHead>
                        <TableHead>Uhrzeit</TableHead>
                        <TableHead>Preis</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Telefon</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {bookings.map((booking) => (
                        <TableRow key={booking.id}>
                          <TableCell className="font-medium">
                            <div>
                              <p>{booking.user_name}</p>
                              <p className="text-sm text-slate-500">{booking.user_email}</p>
                            </div>
                          </TableCell>
                          <TableCell>{booking.service_name}</TableCell>
                          <TableCell>{booking.date}</TableCell>
                          <TableCell>{booking.time}</TableCell>
                          <TableCell className="font-semibold">€{booking.service_price}</TableCell>
                          <TableCell>
                            <Badge className={getStatusColor(booking.status)}>{booking.status}</Badge>
                          </TableCell>
                          <TableCell className="text-sm text-slate-600">
                            {booking.user_phone || 'Nicht angegeben'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  </div>
                )}
              </div>
            </Card>
          </div>
        )}

        {/* Orders Tab */}
        {activeTab === 'orders' && (
          <div>
            <div className="mb-6 lg:mb-8">
              <h1 className="text-2xl lg:text-4xl font-semibold tracking-tight text-slate-900 mb-2">Alle Bestellungen</h1>
              <p className="text-sm lg:text-base text-slate-600">Verwalten Sie Shop-Bestellungen</p>
            </div>

            <Card className="border-slate-100">
              <div className="p-3 lg:p-6">
                {orders.length === 0 ? (
                  <p className="text-slate-600 text-center py-8">Keine Bestellungen vorhanden</p>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Kunde</TableHead>
                        <TableHead>Produkte</TableHead>
                        <TableHead>Gesamtsumme</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Datum</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {orders.map((order) => (
                        <TableRow key={order.id}>
                          <TableCell className="font-medium">
                            <div>
                              <p>{order.user_name}</p>
                              <p className="text-sm text-slate-500">{order.user_email}</p>
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="text-sm">
                              {order.items.map((item, idx) => (
                                <p key={idx}>
                                  {item.product_name} x{item.quantity}
                                </p>
                              ))}
                            </div>
                          </TableCell>
                          <TableCell className="font-semibold">€{order.total.toFixed(2)}</TableCell>
                          <TableCell>
                            <Badge className={getStatusColor(order.status)}>{order.status}</Badge>
                          </TableCell>
                          <TableCell className="text-sm text-slate-600">
                            {new Date(order.created_at).toLocaleDateString('de-DE')}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  </div>
                )}
              </div>
            </Card>
          </div>
        )}

        {/* Blocked Slots Tab */}
        {activeTab === 'blocked' && (
          <div>
            <div className="mb-6 lg:mb-8">
              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
                <div>
                  <h1 className="text-2xl lg:text-4xl font-semibold tracking-tight text-slate-900 mb-2">Gesperrte Zeiten</h1>
                  <p className="text-sm lg:text-base text-slate-600">Blockieren Sie Zeiten oder ganze Tage</p>
                </div>
                <Button
                  onClick={() => setIsBlockModalOpen(true)}
                  className="bg-blue-600 hover:bg-blue-700 text-white w-full sm:w-auto"
                >
                  Zeit/Tag sperren
                </Button>
              </div>
            </div>

            <Card className="border-slate-100">
              <div className="p-3 lg:p-6">
                {blockedSlots.length === 0 ? (
                  <p className="text-slate-600 text-center py-8">Keine gesperrten Zeiten</p>
                ) : (
                  <div className="space-y-3">
                    {blockedSlots.map((slot) => (
                      <div key={slot.id} className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-3 lg:p-4 bg-red-50 border border-red-200 rounded-lg">
                        <div>
                          <p className="font-semibold text-slate-900">
                            {slot.date} {slot.time ? `um ${slot.time} Uhr` : '(Ganzer Tag)'}
                          </p>
                          <p className="text-sm text-slate-600">{slot.reason || 'Gesperrt'}</p>
                        </div>
                        <Button
                          onClick={() => handleUnblockSlot(slot.id)}
                          variant="destructive"
                          size="sm"
                          className="w-full sm:w-auto"
                        >
                          Freigeben
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </Card>
          </div>
        )}

        {/* Stylists Tab */}
        {activeTab === 'stylists' && (
          <div>
            <div className="mb-6 lg:mb-8">
              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
                <div>
                  <h1 className="text-2xl lg:text-4xl font-semibold tracking-tight text-slate-900 mb-2">Stylisten</h1>
                  <p className="text-sm lg:text-base text-slate-600">Verwalten Sie Ihre Salon-Mitarbeiter</p>
                </div>
                <Button
                  onClick={() => openStylistModal()}
                  className="bg-blue-600 hover:bg-blue-700 text-white w-full sm:w-auto"
                >
                  Stylist hinzufügen
                </Button>
              </div>
            </div>

            <Card className="border-slate-100">
              <div className="p-3 lg:p-6">
                {stylists.length === 0 ? (
                  <p className="text-slate-600 text-center py-8">Keine Stylisten vorhanden</p>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6">
                    {stylists.map((stylist) => {
                      // Check if this stylist is the current logged-in admin
                      const isCurrentAdmin = user && stylist.email === user.email;
                      
                      return (
                        <Card key={stylist.id} className={`p-4 lg:p-6 ${!stylist.is_active ? 'opacity-50' : ''}`}>
                          <div className="flex flex-col items-center text-center">
                            {stylist.profile_image_url ? (
                              <img
                                src={getImageUrl(stylist.profile_image_url)}
                                alt={stylist.name}
                                className="w-24 h-24 rounded-full object-cover mb-4"
                              />
                            ) : (
                              <div className="w-24 h-24 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white text-3xl font-bold mb-4">
                                {stylist.name.charAt(0)}
                              </div>
                            )}
                            <h3 className="text-lg font-semibold text-slate-900 mb-1">
                              {stylist.name}
                              {isCurrentAdmin && (
                                <Badge className="ml-2 bg-green-100 text-green-700">Du</Badge>
                              )}
                            </h3>
                            {stylist.email && (
                              <p className="text-sm text-slate-600 mb-2">{stylist.email}</p>
                            )}
                            {stylist.bio && (
                              <p className="text-sm text-slate-600 mb-4">{stylist.bio}</p>
                            )}
                            {!stylist.is_active && (
                              <Badge className="bg-red-100 text-red-700 mb-3">Inaktiv</Badge>
                            )}
                            {/* Allow super_admin to edit their own profile, but prevent regular admins */}
                            {(!isCurrentAdmin || user?.role === 'super_admin') ? (
                              <div className="flex flex-col sm:flex-row gap-2 mt-4">
                                <Button
                                  onClick={() => openStylistModal(stylist)}
                                  size="sm"
                                  className="bg-blue-600 hover:bg-blue-700 w-full sm:w-auto"
                                >
                                  Bearbeiten
                                </Button>
                                {!isCurrentAdmin && (
                                  <Button
                                    onClick={() => handleDeleteStylist(stylist.id)}
                                    size="sm"
                                    variant="destructive"
                                    className="w-full sm:w-auto"
                                  >
                                    Löschen
                                  </Button>
                                )}
                              </div>
                            ) : (
                              <div className="mt-4 p-2 bg-slate-100 rounded text-xs text-slate-600">
                                Du kannst dein eigenes Profil nicht bearbeiten
                              </div>
                            )}
                          </div>
                        </Card>
                      );
                    })}
                  </div>
                )}
              </div>
            </Card>
          </div>
        )}

        {/* Services Tab */}
        {activeTab === 'services' && (
          <div>
            <div className="mb-6 lg:mb-8">
              <h1 className="text-2xl lg:text-4xl font-semibold tracking-tight text-slate-900 mb-2">Services verwalten</h1>
              <p className="text-sm lg:text-base text-slate-600">Bearbeiten Sie Beschreibungen und Bilder für Ihre Services</p>
            </div>

            <Card className="border-slate-100">
              <div className="p-3 lg:p-6">
                {services.length === 0 ? (
                  <p className="text-slate-600 text-center py-8">Keine Services vorhanden</p>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6">
                    {services.map((service) => (
                      <Card key={service.id} className="p-4 lg:p-6 hover:shadow-md transition-shadow">
                        {service.image_url && (
                          <div className="w-full h-32 lg:h-40 mb-4 rounded-lg overflow-hidden">
                            <img
                              src={getImageUrl(service.image_url)}
                              alt={service.name}
                              className="w-full h-full object-cover"
                            />
                          </div>
                        )}
                        <h3 className="text-lg font-semibold text-slate-900 mb-2">{service.name}</h3>
                        <p className="text-sm text-slate-600 mb-3 line-clamp-2">
                          {service.description || 'Keine Beschreibung'}
                        </p>
                        <div className="flex items-center justify-between mb-4">
                          <span className="text-2xl font-bold text-blue-600">€{service.price}</span>
                          <span className="text-sm text-slate-600">{service.duration} Min</span>
                        </div>
                        <Button
                          onClick={() => openServiceModal(service)}
                          className="w-full bg-blue-600 hover:bg-blue-700"
                        >
                          Bearbeiten
                        </Button>
                      </Card>
                    ))}
                  </div>
                )}
              </div>
            </Card>
          </div>
        )}

        {/* Products Tab */}
        {activeTab === 'products' && (
          <div>
            <div className="mb-6 lg:mb-8">
              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
                <div>
                  <h1 className="text-2xl lg:text-4xl font-semibold tracking-tight text-slate-900 mb-2">Shop Produkte</h1>
                  <p className="text-sm lg:text-base text-slate-600">Verwalten Sie Ihre Shop-Produkte</p>
                </div>
                <Button
                  onClick={() => openProductModal()}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Neues Produkt
                </Button>
              </div>
            </div>

            <Card className="border-slate-100">
              <div className="p-3 lg:p-6">
                {products.length === 0 ? (
                  <p className="text-slate-600 text-center py-8">Keine Produkte vorhanden. Fügen Sie ein Produkt hinzu.</p>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 lg:gap-6">
                    {products.map((product) => (
                      <Card key={product.id} className="p-4 hover:shadow-md transition-shadow">
                        <div className="w-full h-32 lg:h-40 mb-4 rounded-lg overflow-hidden bg-slate-100">
                          <img
                            src={getImageUrl(product.image_url) || 'https://images.unsplash.com/photo-1522337360788-8b13dee7a37e?w=400'}
                            alt={product.name}
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <div className="flex items-center justify-between mb-2">
                          <h3 className="text-lg font-semibold text-slate-900">{product.name}</h3>
                          <Badge className={product.in_stock ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}>
                            {product.in_stock ? 'Verfügbar' : 'Ausverkauft'}
                          </Badge>
                        </div>
                        <p className="text-sm text-slate-600 mb-2 line-clamp-2">
                          {product.description || 'Keine Beschreibung'}
                        </p>
                        <div className="flex items-center gap-2 mb-3">
                          <Badge variant="outline" className="text-xs">{product.category}</Badge>
                          <Badge variant="outline" className="text-xs">{product.hair_type}</Badge>
                        </div>
                        <div className="flex items-center justify-between mb-4">
                          <span className="text-xl font-bold text-blue-600">€{product.price}</span>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            onClick={() => openProductModal(product)}
                            variant="outline"
                            className="flex-1"
                          >
                            Bearbeiten
                          </Button>
                          <Button
                            onClick={() => handleDeleteProduct(product.id)}
                            variant="outline"
                            className="text-red-600 hover:bg-red-50 border-red-200"
                          >
                            Löschen
                          </Button>
                        </div>
                      </Card>
                    ))}
                  </div>
                )}
              </div>
            </Card>
          </div>
        )}

        {/* Customers Tab */}
        {activeTab === 'customers' && (
          <div>
            <div className="mb-6 lg:mb-8">
              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
                <div>
                  <h1 className="text-2xl lg:text-4xl font-semibold tracking-tight text-slate-900 mb-2">Registrierte Kunden</h1>
                  <p className="text-sm lg:text-base text-slate-600">Übersicht aller registrierten Benutzer</p>
                </div>
                <Button
                  onClick={() => setIsCustomerModalOpen(true)}
                  className="bg-blue-600 hover:bg-blue-700 text-white w-full sm:w-auto"
                >
                  Kunde hinzufügen
                </Button>
              </div>
            </div>

            <Card className="border-slate-100">
              <div className="p-3 lg:p-6">
                {customers.length === 0 ? (
                  <p className="text-slate-600 text-center py-8">Keine Kunden vorhanden</p>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Name</TableHead>
                        <TableHead>E-Mail</TableHead>
                        <TableHead>Telefon</TableHead>
                        <TableHead>Haartyp</TableHead>
                        <TableHead>Anmerkungen</TableHead>
                        <TableHead>Buchungen</TableHead>
                        <TableHead>Bestellungen</TableHead>
                        <TableHead>Registriert am</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {customers.map((customer) => (
                        <TableRow 
                          key={customer.id}
                          className="cursor-pointer hover:bg-slate-50 transition-colors"
                          onClick={() => {
                            setSelectedCustomer(customer);
                            setEditCustomerForm({
                              name: customer.name,
                              phone: customer.phone || '',
                              hair_type: customer.hair_type || '',
                              notes: customer.notes || ''
                            });
                            setIsCustomerDetailOpen(true);
                            setIsEditingCustomer(false);
                          }}
                        >
                          <TableCell className="font-medium">
                            <div className="flex items-center space-x-2">
                              <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-semibold">
                                {customer.name.charAt(0).toUpperCase()}
                              </div>
                              <span>{customer.name}</span>
                            </div>
                          </TableCell>
                          <TableCell>{customer.email}</TableCell>
                          <TableCell className="text-sm text-slate-600">
                            {customer.phone || '-'}
                          </TableCell>
                          <TableCell>
                            {customer.hair_type ? (
                              <Badge className="bg-purple-100 text-purple-700">{customer.hair_type}</Badge>
                            ) : (
                              '-'
                            )}
                          </TableCell>
                          <TableCell className="text-sm max-w-md">
                            {customer.notes ? (
                              <div className="p-2 bg-amber-50 border border-amber-200 rounded text-slate-700">
                                <div className="line-clamp-2" title={customer.notes}>
                                  {customer.notes}
                                </div>
                              </div>
                            ) : (
                              <span className="text-slate-400">-</span>
                            )}
                          </TableCell>
                          <TableCell className="text-center">
                            <Badge className="bg-blue-100 text-blue-700">
                              {customer.bookings_count || 0}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-center">
                            <Badge className="bg-green-100 text-green-700">
                              {customer.orders_count || 0}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-sm text-slate-600">
                            {customer.created_at ? new Date(customer.created_at).toLocaleDateString('de-DE') : '-'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  </div>
                )}
              </div>
            </Card>
          </div>
        )}

        {/* Billing Tab */}
        {activeTab === 'billing' && (
          <div>
            <div className="mb-6 lg:mb-8">
              <h1 className="text-2xl lg:text-4xl font-semibold tracking-tight text-slate-900 mb-2">Abos & Gutscheine</h1>
              <p className="text-sm lg:text-base text-slate-600">Zahlungen überblicken und Gutscheine direkt im Salon einlösen</p>
            </div>

            <div className="grid sm:grid-cols-3 gap-4 mb-6">
              <Card className="p-5"><p className="text-sm text-slate-500">Aktive Abos</p><p className="text-3xl font-bold mt-1">{billingOverview.summary?.active_memberships || 0}</p></Card>
              <Card className="p-5"><p className="text-sm text-slate-500">Aktive Gutscheine</p><p className="text-3xl font-bold mt-1">{billingOverview.summary?.active_gift_cards || 0}</p></Card>
              <Card className="p-5"><p className="text-sm text-slate-500">Offenes Guthaben</p><p className="text-3xl font-bold mt-1">{Number(billingOverview.summary?.gift_card_balance || 0).toFixed(2).replace('.', ',')} €</p></Card>
            </div>

            <Card className="p-5 lg:p-6 mb-6 border-blue-100">
              <h2 className="text-xl font-semibold mb-4 flex items-center"><Gift className="w-5 h-5 mr-2 text-blue-600" /> Gutschein einlösen</h2>
              <form onSubmit={handleGiftRedemption} className="grid sm:grid-cols-[1fr_180px_auto] gap-3 items-end">
                <div>
                  <Label htmlFor="redeem-code">Gutscheincode</Label>
                  <Input id="redeem-code" className="mt-2 uppercase" placeholder="GALO-XXXXXXXX" value={giftRedemption.code}
                    onChange={(event) => setGiftRedemption({ ...giftRedemption, code: event.target.value })} />
                </div>
                <div>
                  <Label htmlFor="redeem-amount">Betrag in €</Label>
                  <Input id="redeem-amount" className="mt-2" type="number" min="0.01" step="0.01" value={giftRedemption.amount}
                    onChange={(event) => setGiftRedemption({ ...giftRedemption, amount: event.target.value })} />
                </div>
                <Button type="submit" disabled={redeemingGift} className="bg-blue-600 hover:bg-blue-700">
                  {redeemingGift ? 'Wird eingelöst...' : 'Jetzt einlösen'}
                </Button>
              </form>
            </Card>

            <Card className="p-4 lg:p-6 mb-6">
              <h2 className="text-lg font-semibold mb-4">Gutscheine</h2>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader><TableRow><TableHead>Code</TableHead><TableHead>Empfänger</TableHead><TableHead>Wert</TableHead><TableHead>Rest</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
                  <TableBody>
                    {(billingOverview.gift_cards || []).map((card) => (
                      <TableRow key={card.id}>
                        <TableCell className="font-mono font-semibold">{card.code || 'Zahlung offen'}</TableCell>
                        <TableCell><div>{card.recipient_name || '-'}</div><div className="text-xs text-slate-500">{card.recipient_email}</div></TableCell>
                        <TableCell>{Number(card.amount || 0).toFixed(2).replace('.', ',')} €</TableCell>
                        <TableCell className="font-semibold">{Number(card.balance || 0).toFixed(2).replace('.', ',')} €</TableCell>
                        <TableCell><Badge variant={card.status === 'active' ? 'default' : 'secondary'}>{card.status}</Badge></TableCell>
                      </TableRow>
                    ))}
                    {(billingOverview.gift_cards || []).length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-slate-500 py-8">Noch keine Gutscheine</TableCell></TableRow>}
                  </TableBody>
                </Table>
              </div>
            </Card>

            <Card className="p-4 lg:p-6">
              <h2 className="text-lg font-semibold mb-4">Mitgliedschaften</h2>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader><TableRow><TableHead>Plan</TableHead><TableHead>Abrechnung</TableHead><TableHead>Status</TableHead><TableHead>Seit</TableHead></TableRow></TableHeader>
                  <TableBody>
                    {(billingOverview.memberships || []).map((membership) => (
                      <TableRow key={membership.id}>
                        <TableCell className="font-semibold">{membership.plan_id}</TableCell>
                        <TableCell>{membership.billing_interval === 'year' ? 'jährlich' : 'monatlich'}</TableCell>
                        <TableCell><Badge variant={membership.status === 'active' ? 'default' : 'secondary'}>{membership.status}</Badge></TableCell>
                        <TableCell>{membership.created_at ? new Date(membership.created_at).toLocaleDateString('de-DE') : '-'}</TableCell>
                      </TableRow>
                    ))}
                    {(billingOverview.memberships || []).length === 0 && <TableRow><TableCell colSpan={4} className="text-center text-slate-500 py-8">Noch keine Mitgliedschaften</TableCell></TableRow>}
                  </TableBody>
                </Table>
              </div>
            </Card>
          </div>
        )}

        {/* Settings Tab */}
        {activeTab === 'settings' && (
          <div>
            <div className="mb-6 lg:mb-8">
              <h1 className="text-2xl lg:text-4xl font-semibold tracking-tight text-slate-900 mb-2">Einstellungen</h1>
              <p className="text-sm lg:text-base text-slate-600">Verwalten Sie Ihre Salon-Einstellungen und Website-Inhalte</p>
            </div>

            <div className="space-y-4 lg:space-y-6">
              {/* Banner slideshow settings */}
              <Card className="p-4 lg:p-6 border-slate-100">
                <h2 className="text-lg lg:text-xl font-semibold mb-2 flex items-center">
                  <span className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center mr-3">
                    <Sparkles className="w-4 h-4 text-blue-600" />
                  </span>
                  Banner-Slideshow (Startseite)
                </h2>
                <p className="text-sm text-slate-600 mb-4">Bis zu 5 Bilder. Das bisherige Bannerbild bleibt als erstes Bild erhalten. Änderungen an URLs und Reihenfolge bitte speichern; Uploads werden sofort gespeichert.</p>
                <div className="space-y-4">
                  {heroSlidesDraft.map((url, index) => (
                    <div key={index} className="rounded-xl border border-slate-200 p-4 space-y-3">
                      <div className="flex items-center justify-between gap-2">
                        <Label>Bild {index + 1} von {heroSlidesDraft.length}</Label>
                        <div className="flex items-center gap-1">
                          <Button type="button" variant="outline" size="icon" title="Nach links verschieben" aria-label={`Bild ${index + 1} nach links verschieben`}
                            disabled={index === 0 || savingHeroSlides} onClick={() => moveHeroSlide(index, -1)}><ChevronLeft className="h-4 w-4" /></Button>
                          <Button type="button" variant="outline" size="icon" title="Nach rechts verschieben" aria-label={`Bild ${index + 1} nach rechts verschieben`}
                            disabled={index === heroSlidesDraft.length - 1 || savingHeroSlides} onClick={() => moveHeroSlide(index, 1)}><ChevronRight className="h-4 w-4" /></Button>
                          <Button type="button" variant="outline" size="icon" title="Aus Slideshow entfernen" aria-label={`Bild ${index + 1} entfernen`}
                            disabled={heroSlidesDraft.length === 1 || savingHeroSlides}
                            onClick={() => setHeroSlidesDraft((current) => current.filter((_, position) => position !== index))}>
                            <X className="h-4 w-4 text-red-600" />
                          </Button>
                        </div>
                      </div>
                      {url && <img src={getImageUrl(url)} alt={`Vorschau von Bannerbild ${index + 1}`} className="h-40 w-full max-w-lg rounded-lg object-cover border" />}
                      <Input aria-label={`Bild-URL für Bannerbild ${index + 1}`} value={url}
                        onChange={(event) => setHeroSlidesDraft((current) => current.map((item, position) => position === index ? event.target.value : item))}
                        placeholder="https://... oder /uploads/..." />
                      <label className="inline-flex cursor-pointer items-center gap-2 rounded-lg border border-dashed border-blue-400 px-4 py-2 text-sm text-blue-700 hover:bg-blue-50">
                        <Plus className="w-4 h-4" /> {savingHeroSlides ? 'Bitte warten...' : 'Bild hochladen (JPEG, PNG, WEBP)'}
                        <input type="file" accept="image/jpeg,image/png,image/webp" className="sr-only"
                          disabled={savingHeroSlides} onChange={(event) => handleHeroImageUpload(event, index)} />
                      </label>
                    </div>
                  ))}
                  <div className="flex flex-wrap gap-3">
                    <Button type="button" variant="outline" disabled={heroSlidesDraft.length >= 5 || savingHeroSlides}
                      onClick={() => setHeroSlidesDraft((current) => [...current, ''])}>
                      <Plus className="w-4 h-4 mr-2" /> Bild hinzufügen ({heroSlidesDraft.length}/5)
                    </Button>
                    <Button type="button" disabled={savingHeroSlides} className="bg-blue-600 hover:bg-blue-700"
                      onClick={() => saveHeroSlides(heroSlidesDraft)}>
                      {savingHeroSlides ? 'Speichert...' : 'Slideshow speichern'}
                    </Button>
                  </div>
                </div>
              </Card>

              {/* Services Management */}
              <Card className="p-4 lg:p-6 border-slate-100">
                <h2 className="text-lg lg:text-xl font-semibold mb-4 flex items-center">
                  <span className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center mr-3">
                    <Scissors className="w-4 h-4 text-green-600" />
                  </span>
                  Services verwalten
                </h2>
                <p className="text-sm text-slate-600 mb-4">Bearbeiten Sie Preise, Beschreibungen und Bilder Ihrer Services</p>
                <div className="space-y-4">
                  {services.map((service) => (
                    <div key={service.id} className="border rounded-lg p-4 bg-slate-50">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center space-x-3">
                          {service.image_url ? (
                            <img src={getImageUrl(service.image_url)} alt={service.name} className="w-16 h-16 rounded-lg object-cover" />
                          ) : (
                            <div className="w-16 h-16 rounded-lg bg-slate-200 flex items-center justify-center">
                              <Scissors className="w-6 h-6 text-slate-400" />
                            </div>
                          )}
                          <div>
                            <h3 className="font-semibold text-slate-900">{service.name}</h3>
                            <p className="text-sm text-slate-500">{service.duration} Min</p>
                          </div>
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => openServiceModal(service)}
                        >
                          Bearbeiten
                        </Button>
                      </div>
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <span className="text-slate-500">Preis:</span>
                          <span className="ml-2 font-semibold text-green-600">€{service.price}</span>
                        </div>
                        <div>
                          <span className="text-slate-500">Beschreibung:</span>
                          <p className="text-slate-700 truncate">{service.description || 'Keine Beschreibung'}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                  <Button
                    onClick={() => openServiceModal(null)}
                    className="w-full bg-green-600 hover:bg-green-700"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Neuen Service hinzufügen
                  </Button>
                </div>
              </Card>

              {/* Gallery Management */}
              <Card className="p-4 lg:p-6 border-slate-100">
                <h2 className="text-lg lg:text-xl font-semibold mb-4 flex items-center">
                  <span className="w-8 h-8 rounded-full bg-pink-100 flex items-center justify-center mr-3">
                    <Sparkles className="w-4 h-4 text-pink-600" />
                  </span>
                  Galerie verwalten
                </h2>
                <p className="text-sm text-slate-600 mb-4">Laden Sie Bilder für Ihre Salon-Galerie hoch</p>
                
                {/* Upload Button */}
                <div className="mb-6">
                  <label className="cursor-pointer">
                    <div className="border-2 border-dashed border-slate-300 rounded-lg p-6 text-center hover:border-pink-500 hover:bg-pink-50 transition-colors">
                      <input
                        type="file"
                        accept="image/jpeg,image/png,image/webp"
                        multiple
                        onChange={handleGalleryUpload}
                        className="hidden"
                        disabled={uploadingGallery}
                      />
                      <div className="w-12 h-12 rounded-full bg-pink-100 flex items-center justify-center mx-auto mb-3">
                        {uploadingGallery ? (
                          <div className="w-5 h-5 border-2 border-pink-600 border-t-transparent rounded-full animate-spin" />
                        ) : (
                          <Plus className="w-6 h-6 text-pink-600" />
                        )}
                      </div>
                      <p className="font-medium text-slate-700">
                        {uploadingGallery && galleryUploadProgress
                          ? `${galleryUploadProgress.completed} von ${galleryUploadProgress.total} Bildern verarbeitet`
                          : 'Mehrere Bilder auswählen'}
                      </p>
                      <p className="text-sm text-slate-500 mt-1">
                        JPEG, PNG oder WEBP · Mehrfachauswahl möglich
                      </p>
                      {uploadingGallery && galleryUploadProgress?.failed > 0 && (
                        <p className="text-sm text-red-600 mt-2">
                          {galleryUploadProgress.failed} Upload{galleryUploadProgress.failed === 1 ? '' : 's'} fehlgeschlagen
                        </p>
                      )}
                    </div>
                  </label>
                </div>

                {/* Gallery Grid */}
                {galleryImages.length > 0 ? (
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                    {galleryImages.map((image) => (
                      <div key={image.id} className="relative group">
                        <img
                          src={getImageUrl(image.image_url)}
                          alt={image.title || 'Gallery'}
                          className="w-full h-32 object-cover rounded-lg"
                        />
                        <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center">
                          <button
                            onClick={() => handleGalleryDelete(image.id)}
                            className="p-2 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                        {image.title && (
                          <p className="text-xs text-slate-600 mt-1 truncate">{image.title}</p>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-slate-500">
                    <p>Noch keine Bilder in der Galerie</p>
                    <p className="text-sm mt-1">Laden Sie Ihr erstes Bild hoch</p>
                  </div>
                )}
              </Card>

              {/* About Section */}
              <Card className="p-4 lg:p-6 border-slate-100">
                <h2 className="text-lg lg:text-xl font-semibold mb-4 flex items-center">
                  <span className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center mr-3">
                    <Users className="w-4 h-4 text-purple-600" />
                  </span>
                  Über uns Bereich
                </h2>
                <div className="space-y-4">
                  <div>
                    <Label>Über uns Titel</Label>
                    <Input
                      value={settings.about_title || 'Über Salon Galo'}
                      onChange={(e) => setSettings({...settings, about_title: e.target.value})}
                      className="mt-2"
                    />
                  </div>
                  <div>
                    <Label>Über uns Text</Label>
                    <Textarea
                      value={settings.about_text || ''}
                      onChange={(e) => setSettings({...settings, about_text: e.target.value})}
                      className="mt-2"
                      placeholder="Beschreiben Sie Ihren Salon..."
                      rows={4}
                    />
                  </div>
                  <Button
                    onClick={async () => {
                      await handleUpdateSetting('about_title', settings.about_title);
                      await handleUpdateSetting('about_text', settings.about_text);
                      toast.success('Über uns Bereich aktualisiert');
                    }}
                    className="bg-purple-600 hover:bg-purple-700"
                  >
                    Speichern
                  </Button>
                </div>
              </Card>

              {/* Opening Hours */}
              <Card className="p-4 lg:p-6 border-slate-100">
                <h2 className="text-lg lg:text-xl font-semibold mb-4 flex items-center">
                  <span className="w-8 h-8 rounded-full bg-orange-100 flex items-center justify-center mr-3">
                    <Clock className="w-4 h-4 text-orange-600" />
                  </span>
                  Öffnungszeiten
                </h2>
                <div className="space-y-4">
                  <div>
                    <Label>Öffnungszeiten Text</Label>
                    <Textarea
                      value={settings.opening_hours || 'Di-Fr: 11:30-18:00\nSa: 10:00-16:00\nSo-Mo: Geschlossen'}
                      onChange={(e) => setSettings({...settings, opening_hours: e.target.value})}
                      className="mt-2 font-mono text-sm"
                      rows={4}
                    />
                  </div>
                  <Button
                    onClick={() => handleUpdateSetting('opening_hours', settings.opening_hours)}
                    className="bg-orange-600 hover:bg-orange-700"
                  >
                    Speichern
                  </Button>
                </div>
              </Card>

              <Card className="p-4 lg:p-6 border-slate-100">
                <h2 className="text-lg lg:text-xl font-semibold mb-4">Social Media Links</h2>
                <div className="space-y-4">
                  <div>
                    <Label>Instagram URL</Label>
                    <Input
                      value={settings.instagram_url || ''}
                      onChange={(e) => setSettings({...settings, instagram_url: e.target.value})}
                      className="mt-2"
                    />
                    <Button
                      onClick={() => handleUpdateSetting('instagram_url', settings.instagram_url)}
                      className="mt-2 bg-blue-600 hover:bg-blue-700"
                      size="sm"
                    >
                      Speichern
                    </Button>
                  </div>
                  
                  <div>
                    <Label>Facebook URL</Label>
                    <Input
                      value={settings.facebook_url || ''}
                      onChange={(e) => setSettings({...settings, facebook_url: e.target.value})}
                      className="mt-2"
                    />
                    <Button
                      onClick={() => handleUpdateSetting('facebook_url', settings.facebook_url)}
                      className="mt-2 bg-blue-600 hover:bg-blue-700"
                      size="sm"
                    >
                      Speichern
                    </Button>
                  </div>

                  <div>
                    <Label>TikTok URL</Label>
                    <Input
                      value={settings.tiktok_url || ''}
                      onChange={(e) => setSettings({...settings, tiktok_url: e.target.value})}
                      className="mt-2"
                    />
                    <Button
                      onClick={() => handleUpdateSetting('tiktok_url', settings.tiktok_url)}
                      className="mt-2 bg-blue-600 hover:bg-blue-700"
                      size="sm"
                    >
                      Speichern
                    </Button>
                  </div>
                </div>
              </Card>

              <Card className="p-4 lg:p-6 border-slate-100">
                <h2 className="text-lg lg:text-xl font-semibold mb-4">Kontakt</h2>
                <div className="space-y-4">
                  <div>
                    <Label>WhatsApp Nummer</Label>
                    <Input
                      value={settings.whatsapp_number || ''}
                      onChange={(e) => setSettings({...settings, whatsapp_number: e.target.value})}
                      className="mt-2"
                      placeholder="01636849710"
                    />
                    <Button
                      onClick={() => handleUpdateSetting('whatsapp_number', settings.whatsapp_number)}
                      className="mt-2 bg-blue-600 hover:bg-blue-700"
                      size="sm"
                    >
                      Speichern
                    </Button>
                  </div>

                  <div>
                    <Label>Telefonnummer</Label>
                    <Input
                      value={settings.phone_number || ''}
                      onChange={(e) => setSettings({...settings, phone_number: e.target.value})}
                      className="mt-2"
                      placeholder="0228 12345678"
                    />
                    <Button
                      onClick={() => handleUpdateSetting('phone_number', settings.phone_number)}
                      className="mt-2 bg-blue-600 hover:bg-blue-700"
                      size="sm"
                    >
                      Speichern
                    </Button>
                  </div>

                  <div>
                    <Label>E-Mail Adresse</Label>
                    <Input
                      value={settings.email || ''}
                      onChange={(e) => setSettings({...settings, email: e.target.value})}
                      className="mt-2"
                      placeholder="info@salon-galo.de"
                    />
                    <Button
                      onClick={() => handleUpdateSetting('email', settings.email)}
                      className="mt-2 bg-blue-600 hover:bg-blue-700"
                      size="sm"
                    >
                      Speichern
                    </Button>
                  </div>
                </div>
              </Card>

              <Card className="p-4 lg:p-6 border-slate-100">
                <h2 className="text-lg lg:text-xl font-semibold mb-4">Standort</h2>
                <div className="space-y-4">
                  <div>
                    <Label>Google Maps URL</Label>
                    <Input
                      value={settings.google_maps_url || ''}
                      onChange={(e) => setSettings({...settings, google_maps_url: e.target.value})}
                      className="mt-2"
                    />
                    <Button
                      onClick={() => handleUpdateSetting('google_maps_url', settings.google_maps_url)}
                      className="mt-2 bg-blue-600 hover:bg-blue-700"
                      size="sm"
                    >
                      Speichern
                    </Button>
                  </div>
                  
                  <div>
                    <Label>Salon Adresse</Label>
                    <Input
                      value={settings.salon_address || ''}
                      onChange={(e) => setSettings({...settings, salon_address: e.target.value})}
                      className="mt-2"
                    />
                    <Button
                      onClick={() => handleUpdateSetting('salon_address', settings.salon_address)}
                      className="mt-2 bg-blue-600 hover:bg-blue-700"
                      size="sm"
                    >
                      Speichern
                    </Button>
                  </div>
                </div>
              </Card>
            </div>
          </div>
        )}
      </div>

      {/* Block Slot Modal */}
      <Dialog open={isBlockModalOpen} onOpenChange={setIsBlockModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-2xl font-semibold">Zeit/Tag sperren</DialogTitle>
          </DialogHeader>

          <div className="space-y-6">
            <div>
              <Label className="text-base font-medium">Datum wählen</Label>
              <div className="mt-2 flex justify-center">
                <Calendar
                  mode="single"
                  selected={blockDate}
                  onSelect={setBlockDate}
                  disabled={(date) => date < new Date()}
                  locale={de}
                  className="border rounded-xl"
                />
              </div>
            </div>

            <div>
              <Label>Uhrzeit (optional - leer lassen für ganzen Tag)</Label>
              <select
                value={blockTime}
                onChange={(e) => setBlockTime(e.target.value)}
                className="w-full mt-2 p-2 border border-slate-200 rounded-lg"
              >
                <option value="">Ganzer Tag</option>
                {timeSlots.map((slot) => (
                  <option key={slot} value={slot}>{slot}</option>
                ))}
              </select>
            </div>

            <div>
              <Label>Grund (optional)</Label>
              <Textarea
                value={blockReason}
                onChange={(e) => setBlockReason(e.target.value)}
                placeholder="z.B. Urlaub, Feiertag, Schulung"
                className="mt-2"
              />
            </div>

            <Button
              onClick={handleBlockSlot}
              className="w-full bg-red-600 hover:bg-red-700 text-white"
            >
              Zeit sperren
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Stylist Modal */}
      <Dialog open={isStylistModalOpen} onOpenChange={setIsStylistModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-2xl font-semibold">
              {editingStylist ? 'Stylist bearbeiten' : 'Neuer Stylist'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label>Name *</Label>
              <Input
                value={stylistForm.name}
                onChange={(e) => setStylistForm({...stylistForm, name: e.target.value})}
                placeholder="z.B. Joe"
                className="mt-2"
              />
            </div>

            <div>
              <Label>E-Mail</Label>
              <Input
                value={stylistForm.email}
                onChange={(e) => setStylistForm({...stylistForm, email: e.target.value})}
                placeholder="joe@salon.de"
                type="email"
                className="mt-2"
              />
            </div>

            <div>
              <Label>Profilbild</Label>
              <div className="mt-2 space-y-3">
                {stylistForm.profile_image_url && (
                  <div className="flex items-center justify-center">
                    <img
                      src={getImageUrl(stylistForm.profile_image_url)}
                      alt="Preview"
                      className="w-24 h-24 rounded-full object-cover border-2 border-slate-200"
                    />
                  </div>
                )}
                <div className="flex gap-2">
                  <label className="flex-1">
                    <div className="w-full p-2 border-2 border-dashed border-slate-300 rounded-lg text-center cursor-pointer hover:border-blue-500 transition-colors">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleImageUpload}
                        className="hidden"
                      />
                      <span className="text-sm text-slate-600">📸 Bild hochladen</span>
                    </div>
                  </label>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const url = prompt('Oder Bild-URL eingeben:');
                      if (url) setStylistForm({...stylistForm, profile_image_url: url});
                    }}
                    className="px-3"
                  >
                    🔗 URL
                  </Button>
                </div>
              </div>
            </div>

            <div>
              <Label>Bio</Label>
              <Textarea
                value={stylistForm.bio}
                onChange={(e) => setStylistForm({...stylistForm, bio: e.target.value})}
                placeholder="Kurze Beschreibung..."
                className="mt-2"
                rows={3}
              />
            </div>

            <Button
              onClick={handleSaveStylist}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white"
            >
              {editingStylist ? 'Aktualisieren' : 'Hinzufügen'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Service Modal */}
      <Dialog open={isServiceModalOpen} onOpenChange={setIsServiceModalOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-2xl font-semibold">
              Service bearbeiten
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label>Service Name *</Label>
              <Input
                value={serviceForm.name}
                onChange={(e) => setServiceForm({...serviceForm, name: e.target.value})}
                placeholder="z.B. Haarschnitt"
                className="mt-2"
              />
            </div>

            <div>
              <Label>Beschreibung</Label>
              <Textarea
                value={serviceForm.description}
                onChange={(e) => setServiceForm({...serviceForm, description: e.target.value})}
                placeholder="Detaillierte Beschreibung des Services..."
                className="mt-2"
                rows={4}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Preis (€) *</Label>
                <Input
                  type="number"
                  value={serviceForm.price}
                  onChange={(e) => setServiceForm({...serviceForm, price: e.target.value})}
                  placeholder="20"
                  className="mt-2"
                />
              </div>

              <div>
                <Label>Dauer (Min) *</Label>
                <Input
                  type="number"
                  value={serviceForm.duration}
                  onChange={(e) => setServiceForm({...serviceForm, duration: e.target.value})}
                  placeholder="30"
                  className="mt-2"
                />
              </div>
            </div>

            <div>
              <Label>Service-Bild</Label>
              <div className="mt-2 space-y-3">
                {serviceForm.image_url && (
                  <div className="flex items-center justify-center">
                    <img
                      src={serviceForm.image_url}
                      alt="Preview"
                      className="w-full h-48 rounded-lg object-cover border-2 border-slate-200"
                    />
                  </div>
                )}
                <div className="flex gap-2">
                  <label className="flex-1">
                    <div className="w-full p-2 border-2 border-dashed border-slate-300 rounded-lg text-center cursor-pointer hover:border-blue-500 transition-colors">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleServiceImageUpload}
                        className="hidden"
                      />
                      <span className="text-sm text-slate-600">📸 Bild hochladen</span>
                    </div>
                  </label>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const url = prompt('Oder Bild-URL eingeben:');
                      if (url) setServiceForm({...serviceForm, image_url: url});
                    }}
                    className="px-3"
                  >
                    🔗 URL
                  </Button>
                </div>
              </div>
            </div>

            <Button
              onClick={handleSaveService}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white"
            >
              Aktualisieren
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Product Modal */}
      <Dialog open={isProductModalOpen} onOpenChange={setIsProductModalOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-2xl font-semibold">
              {editingProduct ? 'Produkt bearbeiten' : 'Neues Produkt'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label>Produkt Name *</Label>
              <Input
                value={productForm.name}
                onChange={(e) => setProductForm({...productForm, name: e.target.value})}
                placeholder="z.B. Haargel Premium"
                className="mt-2"
              />
            </div>

            <div>
              <Label>Beschreibung</Label>
              <Textarea
                value={productForm.description}
                onChange={(e) => setProductForm({...productForm, description: e.target.value})}
                placeholder="Detaillierte Beschreibung des Produkts..."
                className="mt-2"
                rows={3}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Preis (€) *</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={productForm.price}
                  onChange={(e) => setProductForm({...productForm, price: e.target.value})}
                  placeholder="19.99"
                  className="mt-2"
                />
              </div>

              <div>
                <Label>Kategorie</Label>
                <select
                  value={productForm.category}
                  onChange={(e) => setProductForm({...productForm, category: e.target.value})}
                  className="mt-2 w-full p-2 border border-slate-300 rounded-lg"
                >
                  <option value="Haarpflege">Haarpflege</option>
                  <option value="Styling">Styling</option>
                  <option value="Farbe">Farbe</option>
                  <option value="Werkzeug">Werkzeug</option>
                  <option value="Zubehör">Zubehör</option>
                </select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Haartyp</Label>
                <select
                  value={productForm.hair_type}
                  onChange={(e) => setProductForm({...productForm, hair_type: e.target.value})}
                  className="mt-2 w-full p-2 border border-slate-300 rounded-lg"
                >
                  <option value="alle">Alle Haartypen</option>
                  <option value="lockig">Lockig</option>
                  <option value="glatt">Glatt</option>
                  <option value="afro">Afro</option>
                </select>
              </div>

              <div className="flex items-center mt-6">
                <input
                  type="checkbox"
                  id="in_stock"
                  checked={productForm.in_stock}
                  onChange={(e) => setProductForm({...productForm, in_stock: e.target.checked})}
                  className="w-4 h-4 mr-2"
                />
                <Label htmlFor="in_stock">Auf Lager</Label>
              </div>
            </div>

            <div>
              <Label>Produkt-Bild</Label>
              <div className="mt-2 space-y-3">
                {productForm.image_url && (
                  <div className="flex items-center justify-center">
                    <img
                      src={getImageUrl(productForm.image_url)}
                      alt="Preview"
                      className="w-full h-40 rounded-lg object-cover border-2 border-slate-200"
                    />
                  </div>
                )}
                <div className="flex gap-2">
                  <label className="flex-1">
                    <div className="w-full p-2 border-2 border-dashed border-slate-300 rounded-lg text-center cursor-pointer hover:border-blue-500 transition-colors">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleProductImageUpload}
                        className="hidden"
                      />
                      <span className="text-sm text-slate-600">📷 Bild hochladen</span>
                    </div>
                  </label>
                  <Input
                    value={productForm.image_url}
                    onChange={(e) => setProductForm({...productForm, image_url: e.target.value})}
                    placeholder="Oder Bild-URL eingeben"
                    className="flex-1"
                  />
                </div>
              </div>
            </div>

            <Button
              onClick={handleSaveProduct}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white"
            >
              {editingProduct ? 'Aktualisieren' : 'Erstellen'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Booking Modal */}
      <Dialog open={isBookingModalOpen} onOpenChange={setIsBookingModalOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-2xl font-semibold">
              Neuen Termin anlegen
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label>Kunden E-Mail *</Label>
              <Input
                value={bookingForm.customer_email}
                onChange={(e) => setBookingForm({...bookingForm, customer_email: e.target.value})}
                placeholder="kunde@email.de"
                type="email"
                className="mt-2"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Service *</Label>
                <select
                  value={bookingForm.service_id}
                  onChange={(e) => setBookingForm({...bookingForm, service_id: e.target.value})}
                  className="w-full mt-2 p-2 border border-slate-200 rounded-lg"
                >
                  <option value="">Service wählen</option>
                  {services.map((service) => (
                    <option key={service.id} value={service.id}>
                      {service.name} - €{service.price} ({service.duration} Min)
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <Label>Stylist *</Label>
                <select
                  value={bookingForm.stylist_id}
                  onChange={(e) => setBookingForm({...bookingForm, stylist_id: e.target.value})}
                  className="w-full mt-2 p-2 border border-slate-200 rounded-lg"
                >
                  <option value="">Stylist wählen</option>
                  {stylists.filter(s => s.is_active).map((stylist) => (
                    <option key={stylist.id} value={stylist.id}>
                      {stylist.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Datum *</Label>
                <Input
                  type="date"
                  value={bookingForm.date}
                  onChange={(e) => setBookingForm({...bookingForm, date: e.target.value})}
                  min={new Date().toISOString().split('T')[0]}
                  className="mt-2"
                />
              </div>

              <div>
                <Label>Uhrzeit *</Label>
                <select
                  value={bookingForm.time}
                  onChange={(e) => setBookingForm({...bookingForm, time: e.target.value})}
                  className="w-full mt-2 p-2 border border-slate-200 rounded-lg"
                >
                  <option value="">Uhrzeit wählen</option>
                  {timeSlots.map((slot) => (
                    <option key={slot} value={slot}>{slot}</option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <Label>Notizen</Label>
              <Textarea
                value={bookingForm.notes}
                onChange={(e) => setBookingForm({...bookingForm, notes: e.target.value})}
                placeholder="Zusätzliche Notizen zum Termin..."
                className="mt-2"
                rows={3}
              />
            </div>

            <Button
              onClick={handleSaveBooking}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white"
            >
              Termin anlegen
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Customer Modal */}
      <Dialog open={isCustomerModalOpen} onOpenChange={setIsCustomerModalOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-2xl font-semibold">
              Neuer Kunde
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label>Name *</Label>
              <Input
                value={customerForm.name}
                onChange={(e) => setCustomerForm({...customerForm, name: e.target.value})}
                placeholder="Max Mustermann"
                className="mt-2"
              />
            </div>

            <div>
              <Label>E-Mail *</Label>
              <Input
                type="email"
                value={customerForm.email}
                onChange={(e) => setCustomerForm({...customerForm, email: e.target.value})}
                placeholder="max@example.com"
                className="mt-2"
              />
            </div>

            <div>
              <Label>Passwort *</Label>
              <Input
                type="password"
                value={customerForm.password}
                onChange={(e) => setCustomerForm({...customerForm, password: e.target.value})}
                placeholder="Mindestens 6 Zeichen"
                className="mt-2"
              />
            </div>

            <div>
              <Label>Telefon</Label>
              <Input
                value={customerForm.phone}
                onChange={(e) => setCustomerForm({...customerForm, phone: e.target.value})}
                placeholder="+49 123 456789"
                className="mt-2"
              />
            </div>

            <div>
              <Label>Haartyp</Label>
              <select
                value={customerForm.hair_type}
                onChange={(e) => setCustomerForm({...customerForm, hair_type: e.target.value})}
                className="w-full mt-2 p-2 border border-slate-200 rounded-lg"
              >
                <option value="">Bitte wählen</option>
                <option value="Normal">Normal</option>
                <option value="Trocken">Trocken</option>
                <option value="Fein">Fein</option>
                <option value="Glatt">Glatt</option>
                <option value="Fettend">Fettend</option>
                <option value="Schuppig">Schuppig</option>
                <option value="Strapaziert">Strapaziert</option>
              </select>
            </div>

            <div>
              <Label>Anmerkungen / Rezeptur Info</Label>
              <Textarea
                value={customerForm.notes}
                onChange={(e) => setCustomerForm({...customerForm, notes: e.target.value})}
                placeholder="z.B. Allergien, verwendete Produkte, spezielle Wünsche, Farbrezepturen..."
                className="mt-2"
                rows={4}
              />
              <p className="text-xs text-slate-500 mt-1">Nur für Admins sichtbar</p>
            </div>

            <Button
              onClick={handleSaveCustomer}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white"
            >
              Kunde anlegen
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Booking Modal */}
      <Dialog open={isBookingModalOpen} onOpenChange={setIsBookingModalOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-2xl font-semibold">
              Neuer Termin
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label>Kunden E-Mail *</Label>
              <Input
                type="email"
                value={bookingForm.customer_email}
                onChange={(e) => setBookingForm({...bookingForm, customer_email: e.target.value})}
                placeholder="kunde@example.com"
                className="mt-2"
              />
              <p className="text-xs text-slate-500 mt-1">Der Kunde muss bereits registriert sein</p>
            </div>

            <div>
              <Label>Service *</Label>
              <select
                value={bookingForm.service_id}
                onChange={(e) => setBookingForm({...bookingForm, service_id: e.target.value})}
                className="w-full mt-2 p-2 border border-slate-200 rounded-lg"
              >
                <option value="">Bitte wählen</option>
                {services.map((service) => (
                  <option key={service.id} value={service.id}>
                    {service.name} - €{service.price} ({service.duration} Min)
                  </option>
                ))}
              </select>
            </div>

            <div>
              <Label>Stylist *</Label>
              <select
                value={bookingForm.stylist_id}
                onChange={(e) => setBookingForm({...bookingForm, stylist_id: e.target.value})}
                className="w-full mt-2 p-2 border border-slate-200 rounded-lg"
              >
                <option value="">Bitte wählen</option>
                {stylists.map((stylist) => (
                  <option key={stylist.id} value={stylist.id}>
                    {stylist.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Datum *</Label>
                <Input
                  type="date"
                  value={bookingForm.date}
                  onChange={(e) => setBookingForm({...bookingForm, date: e.target.value})}
                  className="mt-2"
                />
              </div>

              <div>
                <Label>Uhrzeit *</Label>
                <Input
                  type="time"
                  value={bookingForm.time}
                  onChange={(e) => setBookingForm({...bookingForm, time: e.target.value})}
                  className="mt-2"
                />
              </div>
            </div>

            <div>
              <Label>Notizen</Label>
              <Textarea
                value={bookingForm.notes}
                onChange={(e) => setBookingForm({...bookingForm, notes: e.target.value})}
                placeholder="Zusätzliche Informationen..."
                className="mt-2"
                rows={3}
              />
            </div>

            <Button
              onClick={handleSaveBooking}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white"
            >
              Termin anlegen
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Customer Detail Modal */}
      <Dialog open={isCustomerDetailOpen} onOpenChange={setIsCustomerDetailOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-2xl font-semibold flex items-center justify-between">
              <span>Kunden-Details</span>
              {!isEditingCustomer && (
                <Button
                  onClick={() => setIsEditingCustomer(true)}
                  variant="outline"
                  size="sm"
                  className="ml-4"
                >
                  Bearbeiten
                </Button>
              )}
            </DialogTitle>
          </DialogHeader>

          {selectedCustomer && (
            <div className="space-y-4">
              {!isEditingCustomer ? (
                // View Mode
                <div className="space-y-4">
                  <div className="flex items-center space-x-4">
                    <div className="w-16 h-16 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 text-2xl font-bold">
                      {selectedCustomer.name.charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold text-slate-900">{selectedCustomer.name}</h3>
                      <p className="text-slate-600">{selectedCustomer.email}</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4 pt-4 border-t">
                    <div>
                      <Label className="text-slate-600">Telefon</Label>
                      <p className="text-slate-900 font-medium mt-1">
                        {selectedCustomer.phone || '-'}
                      </p>
                    </div>

                    <div>
                      <Label className="text-slate-600">Haartyp</Label>
                      <p className="mt-1">
                        {selectedCustomer.hair_type ? (
                          <Badge className="bg-purple-100 text-purple-700">
                            {selectedCustomer.hair_type}
                          </Badge>
                        ) : (
                          '-'
                        )}
                      </p>
                    </div>

                    <div>
                      <Label className="text-slate-600">Buchungen</Label>
                      <p className="mt-1">
                        <Badge className="bg-blue-100 text-blue-700">
                          {selectedCustomer.bookings_count || 0}
                        </Badge>
                      </p>
                    </div>

                    <div>
                      <Label className="text-slate-600">Bestellungen</Label>
                      <p className="mt-1">
                        <Badge className="bg-green-100 text-green-700">
                          {selectedCustomer.orders_count || 0}
                        </Badge>
                      </p>
                    </div>
                  </div>

                  <div className="pt-4 border-t">
                    <Label className="text-slate-600">Registriert am</Label>
                    <p className="text-slate-900 mt-1">
                      {selectedCustomer.created_at ? 
                        new Date(selectedCustomer.created_at).toLocaleDateString('de-DE', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric'
                        }) : '-'}
                    </p>
                  </div>

                  {selectedCustomer.notes && (
                    <div className="pt-4 border-t">
                      <Label className="text-slate-600">Anmerkungen / Rezeptur Info</Label>
                      <div className="mt-2 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                        <p className="text-slate-900 whitespace-pre-line">{selectedCustomer.notes}</p>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                // Edit Mode
                <div className="space-y-4">
                  <div>
                    <Label>Name *</Label>
                    <Input
                      value={editCustomerForm.name}
                      onChange={(e) => setEditCustomerForm({...editCustomerForm, name: e.target.value})}
                      className="mt-2"
                    />
                  </div>

                  <div>
                    <Label>E-Mail</Label>
                    <Input
                      value={selectedCustomer.email}
                      disabled
                      className="mt-2 bg-slate-100"
                    />
                    <p className="text-xs text-slate-500 mt-1">E-Mail kann nicht geändert werden</p>
                  </div>

                  <div>
                    <Label>Telefon</Label>
                    <Input
                      value={editCustomerForm.phone}
                      onChange={(e) => setEditCustomerForm({...editCustomerForm, phone: e.target.value})}
                      placeholder="+49 123 456789"
                      className="mt-2"
                    />
                  </div>

                  <div>
                    <Label>Haartyp</Label>
                    <select
                      value={editCustomerForm.hair_type}
                      onChange={(e) => setEditCustomerForm({...editCustomerForm, hair_type: e.target.value})}
                      className="w-full mt-2 p-2 border border-slate-200 rounded-lg"
                    >
                      <option value="">Bitte wählen</option>
                      <option value="Normal">Normal</option>
                      <option value="Trocken">Trocken</option>
                      <option value="Fein">Fein</option>
                      <option value="Glatt">Glatt</option>
                      <option value="Fettend">Fettend</option>
                      <option value="Schuppig">Schuppig</option>
                      <option value="Strapaziert">Strapaziert</option>
                    </select>
                  </div>

                  <div>
                    <Label>Anmerkungen / Rezeptur Info</Label>
                    <Textarea
                      value={editCustomerForm.notes}
                      onChange={(e) => setEditCustomerForm({...editCustomerForm, notes: e.target.value})}
                      placeholder="z.B. Allergien, verwendete Produkte, spezielle Wünsche, Farbrezepturen..."
                      className="mt-2"
                      rows={4}
                    />
                  </div>

                  <div className="flex gap-2 pt-4">
                    <Button
                      onClick={handleUpdateCustomer}
                      className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
                    >
                      Speichern
                    </Button>
                    <Button
                      onClick={() => setIsEditingCustomer(false)}
                      variant="outline"
                      className="flex-1"
                    >
                      Abbrechen
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDashboard;
